<template>
  <div class="input-group input-group--select mb-3">
    <label
      v-if="label"
      class="caption"
    >
      {{ label }}<span v-if="required">*</span>
    </label>
    <div
      v-if="caption"
      class="caption mb-1 error--text"
      v-html="`<div>${caption}</div>`"
    />
    <v-layout
      :column="breakpoint.smAndDown"
      :align-center="!breakpoint.smAndDown"
      style="width:100%"
    >
      <v-flex
        v-if="hasPreview"
        sm3
        xs12
        class="photo"
      >
        <img
          v-img:group="{ src: photo.source_url }"
          v-if="newlyCreatedPreview || getPreview(photo)"
          :src="newlyCreatedPreview || getPreview(photo)"
          :alt="photo.fileName"
          style="max-width: 100%"
        >
        <img
          v-else
          :src="preview"
          style="max-width: 100%"
          class="preview"
          @click="input"
        >
      </v-flex>
      <v-flex
        :class="progressCellClass"
      >
        <v-progress-linear
          v-if="loading"
          :value="progress"
          height="15"
          color="primary"
          style="position: relative"
        />
        <span
          v-else-if="error"
          class="error--text"
        >
          {{ error }}
        </span>
      </v-flex>
      <v-flex
        :class="['text-xs-right', photoButtonsClass ]"
      >
        <cp-camera
          ref="cpCamera"
          :explanation="explanation"
          :overlay-image-key="`PHOTO_${name.toUpperCase()}`"
          @uploadCapture="upload"
        />
        <input
          ref="input"
          :name="name"
          type="file"
          accept="image/*"
          capture="camera"
          style="display: none"
          @change="upload"
        >
        <div :class="[{ 'button-container' : isMobile }]">
          <v-btn
            :loading="loading"
            :block="!isMobile"
            :class="['my-0 ml-0', { 'mr-0' : !showPhotoSelectionButton}]"
            color="primary"
            large
            outline
            @click="input"
          >
            <div class="ml-1 cp-photo_camera">撮影</div>
          </v-btn>
          <v-btn
            v-if="!!showPhotoSelectionButton"
            :class="isMobile ? 'my-0' : 'mt-2'"
            color="primary"
            large
            outline
            block
            @click="selectPhoto"
          >
            <div class="ml-1 cp-photo_selection">写真を選択</div>
          </v-btn>
        </div>
        <v-btn
          v-if="removable && photo.id"
          :loading="removeLoading"
          name="remove_photo"
          color="error"
          class="mb-0 mt-2"
          large
          block
          outline
          @click="remove"
        >写真を削除</v-btn>
      </v-flex>
    </v-layout>
  </div>
</template>

<style scoped>
.photo:hover, .preview:hover {
  cursor: pointer;
}

.button-container {
  display: flex;
  justify-content: space-between;
}

.button-container > .v-btn {
  flex: 1; /* Make buttons take up equal space */
  /* min-width: 100px; */
}
</style>

<script>
import isWebview from 'is-ua-webview';
import { uploadFile } from 'cp-ui/src/upload';
import {
  GET_PHOTOS,
  GET_REMAINING_FIELDS,
} from '../../queries';
import { nonResizeImages } from '../../options';

const CpCamera = () => import('camera-ui/src/components/CpCamera');

export default {
  components: {
    CpCamera,
  },
  props: {
    name: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    confidential: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    removable: {
      type: Boolean,
      default: false,
    },
    caption: {
      type: String,
      default: '',
    },
    preview: {
      type: String,
      default: '',
    },
    photo: {
      type: Object,
      default() {
        return {
          id: 0,
          source_name: '',
          size: 0,
          preview_url: '',
          source_url: '',
          height: 0,
          width: 0,
        };
      },
    },
    nameMaxLength: {
      type: Number,
      default: 15,
    },
    error: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      removeLoading: false,
      progress: 0,
      file: this.photo,
      newlyCreatedPreview: null,
      dialog: false,
    };
  },
  computed: {
    hasPreview() {
      return this.preview || (this.photo && this.photo.url)
          || this.newlyCreatedPreview;
    },
    progressCellClass() {
      return [
        'py-2',
        {
          'pl-3': this.hasPreview && !this.breakpoint.smAndDown,
          'pr-3': !this.breakpoint.smAndDown,
        },
      ];
    },
    explanation() {
      if (this.description) {
        return [this.description];
      }
      return [this.label === '' ? '追加写真' : this.label, this.caption];
    },
    isApp() {
      return isWebview(navigator.userAgent);
    },
    selectedPhotoName() {
      if (this.photo && this.photo.file && this.photo.file.originalFile) {
        return this.photo.file.originalFile.originalName;
      }
      return '';
    },
    photoButtonsClass() {
      return this.isMobile ? 'md3' : 'md2';
    },
    mobileAndTabletCheck() {
      return this.$store.state.isMobile;
    },
    showPhotoSelectionButton() {
      return this.isPhotosTab && this.$store.state.showPhotoSelectionButton;
    },
    isPhotosTab() {
      return this.$route.name === 'photos';
    },
  },
  methods: {
    getPreview(photo) {
      if (this.preview && !this.preview.includes('static')) return this.preview;

      return photo.url || '';
    },
    cutFileName(name) {
      if (name.length < this.nameMaxLength) {
        return name;
      }

      return `...${name.substr(name.length - this.nameMaxLength, this.nameMaxLength)}`;
    },
    input() {
      if (this.mobileAndTabletCheck && !this.isApp) {
        const cpCamera = this.$refs.cpCamera;
        cpCamera.takePhoto();
        return;
      }
      this.$refs.input.click();
    },
    async uploadQuery(file) {
      await uploadFile(
        {
          file,
          $apollo: this.$apollo,
          confidential: this.confidential,
          refetchQueries: [{
            query: GET_PHOTOS,
            variables: { id: parseInt(this.$route.params.id, 10) },
          }, {
            query: GET_REMAINING_FIELDS,
            fetchPolicy: 'network-only',
            variables: {
              appointmentId: parseInt(this.$route.params.id, 10),
              tabs: ['Owner', 'Basic', 'Equipment', 'SalesPoints', 'Photos',
                'Damages', 'Attention', 'Repairs', 'ConfirmForSelfIns', 'Evaluation'],
            },
          }],
          variables: {
            appointmentId: parseInt(this.$route.params.id, 10),
            type: `PHOTO_${this.name.toUpperCase()}`,
            description: this.description,
            originalFileData: {
              originalName: file.name,
            },
          },
          onError: this.onErrorUpload,
          onUploadProgress: (event) => {
            this.progress = (event.loaded / event.total) * 100;
          },
        },
      );
      this.$emit('finished');
      this.onSuccessUpload({});
    },
    async upload({ fileData }) {
      this.loading = true;

      if (fileData && !this.isApp) {
        const resizedData = nonResizeImages.includes(this.name)
          ? fileData : this.$refs.cpCamera.resizeCanvas(fileData);

        resizedData.toBlob((blob) => {
          const imageFile = new File([blob], 'canvas.jpg', { type: 'image/jpeg' });
          this.uploadQuery(imageFile);
        }, 'image/jpeg');
      } else {
        this.uploadQuery(this.$refs.input.files[0]);
      }
    },
    onErrorUpload({ status }) {
      this.loading = false;
      this.progress = 0;
      this.$emit('upload-error', { status });
    },
    onSuccessUpload(file) {
      this.newlyCreatedPreview = file.preview_url;
      this.loading = false;
      this.progress = 0;
    },
    change() {
      this.$emit('change', this.$refs.input.files[0]);
    },
    remove() {
      this.removeLoading = true;
      this.$emit('remove');
    },
    selectPhoto() {
      this.$emit('open-photo-selection', { name: this.name, label: this.label, selectedPhoto: [this.selectedPhotoName] });
    },
  },
};
</script>

<style scoped>
.cp-photo_camera {
  position: relative;
  padding-left: 10px;
}

.cp-photo_camera::before {
  content: '';
  position: absolute;
  left: -10px;
  top: 55%;
  transform: translateY(-50%);
  width: 16px; /* Adjust based on icon size */
  height: 16px; /* Adjust based on icon size */
  background-image: url('~@/assets/icons/icon-camera.svg');
  background-size: contain;
  background-repeat: no-repeat;
}

.cp-photo_selection {
  position: relative;
  padding-left: 10px;
}

.cp-photo_selection::before {
  content: '';
  position: absolute;
  left: -10px;
  top: 55%;
  transform: translateY(-50%);
  width: 16px; /* Adjust based on icon size */
  height: 16px; /* Adjust based on icon size */
  background-image: url('~@/assets/icons/icon-image.svg');
  background-size: contain;
  background-repeat: no-repeat;
}


</style>



// WEBPACK FOOTER //
// src/components/elements/CpPhoto.vue